import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

import { OutgoingMembershipProductDto } from 'api/dtos/memberships/products';
import ProductCardComponent from 'components/checkout/ProductCard';
import { useCheckout } from 'features/organizations/memberships/checkout/context';
import { isProductDisabled } from 'features/organizations/memberships/checkout/helpers/isProductDisabled';
import useProductTimeGateText from 'hooks/useProductTimeGateText';
import ProductStatusChip from 'components/checkout/ProductStatusChip';
import MembershipStatusTag from 'components/MembershipStatusTag';

function ProductCard({
  product,
  onSelect,
  isSelected
}: {
  product: OutgoingMembershipProductDto;
  onSelect: (product: OutgoingMembershipProductDto) => void;
  isSelected: boolean;
}) {
  const router = useRouter();
  const { t } = useTranslation(['checkout']);
  const { checkoutSession, userMemberships } = useCheckout();
  const timeGateText = useProductTimeGateText({
    validFrom: product.validFrom,
    validUntil: product.validUntil
  });

  const currentMembership = userMemberships.find(
    (membership) =>
      membership.productId === product.product.id &&
      membership.status !== 'cancelled'
  );

  const isUserMember = Boolean(currentMembership);

  /**
   * Determines whether the product is disabled based on various conditions:
   *  - Checkout session is currently loading
   *  - Product is disabled
   */
  const isDisabled =
    checkoutSession.isCheckoutDisabled ||
    checkoutSession.isLoadingCheckout ||
    isProductDisabled(product);

  const onClick = () => {
    if (isUserMember) {
      router.push(`/memberships/${currentMembership.id}`);
    }

    if (isDisabled || isSelected || isUserMember) {
      return;
    }

    onSelect(product);
  };

  return (
    <ProductCardComponent
      isSelected={isSelected}
      isDisabled={isDisabled}
      onClick={onClick}
      imageUrl={product.product.imageUrl}
      isPrivate={false}
      name={product.product.name}
      description={product.product.description}
      price={product.product.price}
      discountData={null}
      valueProp={null}
      Header={
        isUserMember ? (
          <MembershipStatusTag status={currentMembership.status} />
        ) : null
      }
      AdditionalProductInfo={
        <>
          {!isUserMember && (
            <ProductStatusChip
              startDate={product.product.startDate}
              endDate={product.product.endDate}
              isDisabled={isProductDisabled(product)}
            />
          )}
        </>
      }
      priceDetails={t('checkout:oneTimeCharge')}
      timeGate={timeGateText}
      Actions={
        <>
          {isUserMember ? (
            <div
              className={classNames(
                'flex items-center justify-center rounded-full bg-primary-200 w-[32px] h-[32px] text-lg text-primary-700'
              )}
            >
              <i className="fa fa-cog" />
            </div>
          ) : (
            <div
              className={classNames(
                'flex items-center justify-center rounded-full border-2 border-[#202020] w-[32px] h-[32px]',
                isSelected && 'bg-[#202020]'
              )}
            >
              {isSelected && (
                <div className="w-[14px] h-[14px] rounded-full bg-white" />
              )}
            </div>
          )}
        </>
      }
    />
  );
}

export default ProductCard;
